import { useEffect, useState } from "react";
import api from "../../../utils/axiosInstance";
import AllPricesTable from "./AllPricesTable";
import PriceListForm from "../../admin/Vendors/VendorDetails/PriceList/PriceListForm";
import { toast } from "../../../components/Toast/toast";

const vendorLabel = (v) => {
  const primaryName = `${v.primary_users?.first_name || ""} ${
    v.primary_users?.last_name || ""
  }`.trim();
  const code = `VDR-${String(v.id).padStart(3, "0")}`;
  const name =
    v.company_name || v.legal_entity || primaryName || v.auth?.email || `Vendor #${v.id}`;
  return `${code} — ${name}`;
};

const VendorPricesPage = () => {
  const [prices, setPrices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // view: "list" | "add"
  const [view, setView] = useState("list");
  const [addVendorId, setAddVendorId] = useState("");

  const fetchPrices = async () => {
    try {
      setLoading(true);
      const res = await api.get("/vendor-price-list", { withCredentials: true });
      setPrices(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch vendor prices");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await api.get("/vendors", { withCredentials: true });
      setVendors(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPrices();
    fetchVendors();
  }, []);

  const handleSave = async (data) => {
    try {
      await api.post("/vendor-price-list", data, { withCredentials: true });
      setView("list");
      setAddVendorId("");
      fetchPrices();
    } catch (err) {
      console.error("Error saving price:", err);
      toast.error(err.response?.data?.message || "Failed to save vendor price");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return <div className="text-center mt-10 text-red-600">{error}</div>;

  if (view === "add") {
    return (
      <div className="space-y-4">
        <div className="max-w-md">
          <label
            htmlFor="add-vendor-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Vendor <span className="text-red-500">*</span>
          </label>
          <select
            id="add-vendor-select"
            value={addVendorId}
            onChange={(e) => setAddVendorId(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Select a vendor —</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {vendorLabel(v)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Choose the vendor you want to add a price for.
          </p>
        </div>

        {addVendorId ? (
          <PriceListForm
            key={addVendorId}
            vendorId={addVendorId}
            onCancel={() => {
              setView("list");
              setAddVendorId("");
            }}
            onSave={handleSave}
          />
        ) : (
          <button
            onClick={() => {
              setView("list");
              setAddVendorId("");
            }}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded"
          >
            Cancel
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Prices</h1>
        <button
          onClick={() => setView("add")}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
        >
          Add Price
        </button>
      </div>

      <p className="text-sm text-gray-600">
        Showing <span className="font-semibold">{prices.length}</span> items.
      </p>

      <AllPricesTable data={prices} />
    </div>
  );
};

export default VendorPricesPage;
